import { createAsyncThunk } from "@reduxjs/toolkit";
import { hasDisplayableError } from "../utils/getErrorMessage";

/**
 * Marker meta the central error middleware (appErrorMiddleware.js) keys on.
 * Only thunks built with createAppAsyncThunk carry it, so the middleware
 * surfaces THEIR failures centrally without also catching the hand-migrated
 * sites/pages thunks (SCRUM-596/722), which still dispatch GET_ERRORS
 * themselves and would otherwise be surfaced twice.
 */
export const APP_ERROR_META = { appError: true };

/**
 * The thunk-API types every app thunk shares (SCRUM-1995). `rejectValue` is the
 * displayable error body or `{ message }` envelope built below; `rejectedMeta`
 * is the marker. State and dispatch stay untyped here on purpose: typing them
 * with RootState / AppDispatch would make store.ts's RootState depend on the
 * thunks that the reducers import, a circular type.
 */
const createAsyncThunkWithAppTypes = createAsyncThunk.withTypes<{
  rejectValue: unknown;
  rejectedMeta: typeof APP_ERROR_META;
}>();

/**
 * `createAsyncThunk` with the two SCRUM-840 write-pilot conventions baked in:
 *
 *  1. Any error the payload creator throws (an axios rejection from the shared
 *     `api` instance, or a bug) is converted to
 *     `rejectWithValue(displayableError, APP_ERROR_META)`. The value is the
 *     server error body when present (`err.response.data`), else a `{ message }`
 *     envelope — the exact shapes `GlobalNotifications` already renders. The
 *     marker meta lets `appErrorMiddleware` feed it into the GET_ERRORS hub once.
 *  2. The payload creator therefore never needs its own try/catch just to reach
 *     the error hub: it returns its success value and lets a throw propagate.
 *
 * Callers dispatch and `await …unwrap()` in a try/catch, running their
 * consequential success side-effects (toast / redirect / close modal /
 * optimistic update) only after the await resolves — never fire-and-forget —
 * so a failed request no longer produces a false success. On rejection
 * `.unwrap()` throws the displayable payload; the central hub has already
 * toasted it, so the caller's catch only needs to skip the success path.
 *
 * A creator that wants a bespoke rejection can still call `rejectWithValue`
 * itself; an explicit `rejectWithValue` return passes through unchanged (only
 * thrown errors are wrapped), though it will not carry the marker meta unless
 * the creator adds it.
 */
const createAppAsyncThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: (arg: ThunkArg, thunkAPI: any) => Promise<Returned>,
  options?: Parameters<typeof createAsyncThunk>[2]
) =>
  createAsyncThunkWithAppTypes<Returned, ThunkArg>(
    typePrefix,
    async (arg, thunkAPI) => {
      try {
        return await payloadCreator(arg, thunkAPI);
      } catch (err) {
        // Use the server body only when the hub would actually SHOW it. `??` alone let
        // an empty `{}` body through, and `hasDisplayableError({})` is false — that
        // shape means "cleared" to the errors slice — so such a failure reached the hub
        // and surfaced nothing. It went unnoticed while every caller still had its own
        // fallback toast; SCRUM-1039 removes those, so the envelope has to hold here.
        const body = (err as any)?.response?.data;
        return thunkAPI.rejectWithValue(
          hasDisplayableError(body)
            ? body
            : { message: (err as any)?.message ?? "Request failed" },
          APP_ERROR_META
        );
      }
    },
    options as any
  );

export default createAppAsyncThunk;
